import { EventAggregator } from "oj-eventaggregator";
import { debounce } from "oj-promise-utils";
import { parseOption } from "../components/footer/util";
import { BoogStraal, HoekVerhouding, SpoorStaafProfiel, WisselLigger, WisselType } from "./configuration";
import { _findByAnswers, _findClosest, filter, find, getBaseProductList } from "./data";
import { smeerlozeConstructie } from "./data/lookup";
import { IDX } from "./data/prorail";
import { questions } from "./data/questions";
import { getText } from "./translations";
import { onderlegSet } from "./data/onderleg_platen";
import { getLocalStorageValue, hasLocalStorageValue, setLocalStorageValue, useLocalStorage } from "./localstrorage";
export const smMap = {
    "Austroroll": "L_LUBRICANT_FREE_AUSTROROLL",
    "Delta-Switch": "L_LUBRICANT_FREE_DELTA_SWITCH",
    "EKOS/Zonder": "L_LUBRICANT_FREE_EKOS"
};
export const pmMap = {
    0: "HT",
    1: "HT",
    2: "HT",
    3: "HT",
    4: "MN",
    5: "MN",
    6: "MN",
};
export class State extends EventAggregator {
    static URL_VERSION = "2";
    static URL_KEYS = {
        version: "v",
        type: "wt",
        wisselLigger: "wl",
        hoekVerhouding: "hv",
        spoorStaafProfiel: "sp",
        boogStraal: "br",
        smeerlozeConstructie: "sm",
        puntStukMateriaal: "pm",
        article: "sa",
        answer: "sq",
        prorailKey: "pk",
        prorailEquipmentId: "peid",
        crosssellArticle: "ra",
        mobileTrackService: "mts",
        returnOldProduct: "rop",
    };
    static URL_MANAGED_KEYS = Object.values(State.URL_KEYS);
    suppressUrlSync = false;
    wissel;
    articles = {};
    crosssellArticles = {};
    tabs = [];
    products = {};
    answers = {};
    mobileTrackService = true;
    returnOldProduct = false;
    default = {
        type: WisselType[0],
        wisselLigger: WisselLigger[0],
        hoekVerhouding: HoekVerhouding[0],
        spoorStaafProfiel: SpoorStaafProfiel[0],
        boogStraal: BoogStraal[0],
    };
    prevUrlSignature;
    prorailChoice;
    constructor() {
        super();
        this.wissel = {
            type: {
                key: "type",
                label: "SWITCH_TYPE",
                value: this.default.type,
                options: WisselType,
                icon: ["icons/wisseltype.svg", "icons/wisseltype-active.svg"],
                update: this.setWisselType.bind(this),
            },
            wisselLigger: {
                key: "wisselLigger",
                label: "SLEEPER_TYPE",
                value: this.default.wisselLigger,
                options: WisselLigger,
                icon: ["icons/dwarsligger.svg", "icons/dwarsligger-active.svg"],
                update: this.setWisselLigger.bind(this),
            },
            hoekVerhouding: {
                key: "hoekVerhouding",
                label: "ANGLE_RATIO",
                value: this.default.hoekVerhouding,
                options: HoekVerhouding,
                icon: ["icons/hoekverhouding.svg", "icons/hoekverhouding-active.svg"],
                update: this.setHoekVerhouding.bind(this),
            },
            spoorStaafProfiel: {
                key: "spoorStaafProfiel",
                label: "RAIL_PROFILE",
                value: this.default.spoorStaafProfiel,
                options: SpoorStaafProfiel,
                icon: ["icons/spoorstaafprofiel.svg", "icons/spoorstaafprofiel-active.svg"],
                update: this.setSpoorStaafProfiel.bind(this),
            },
            boogStraal: {
                key: "boogStraal",
                label: "RADIUS",
                value: this.default.boogStraal,
                options: BoogStraal,
                icon: ["icons/boogstraal.svg", "icons/boogstraal-active.svg"],
                update: this.setBoogStraal.bind(this),
            }
        };
        this.rebuild();
        this.load()
            .then(() => {
            this.onMultiple(["wissel:change", "wisselLigger:change", "hoekVerhouding:change", "spoorStaafProfiel:change", "boogStraal:change"], (d, e) => {
                this.rebuildAsync();
            });
            this.onMultiple(["answers:change"], (d, e) => {
                this.rebuildVariants();
            });
            this.on("*", (d, e) => {
                if (!e.includes("change") || this.suppressUrlSync)
                    return;
                const signature = this.getStateSignature();
                if (signature === this.prevUrlSignature)
                    return;
                this.prevUrlSignature = signature;
                this.syncUrlWithState("replace");
                this.syncLocalStorageWithState();
            });
            window.addEventListener("popstate", () => {
                this.restoreFromCurrentUrl(true);
            });
            this.syncUrlWithState("replace");
            this.syncLocalStorageWithState();
            this.emit("loaded", {});
        });
    }
    async load() {
        try {
            if (window.location.search) {
                const loadedFromUrl = this.restoreFromCurrentUrl(false);
                if (loadedFromUrl) {
                    this.syncLocalStorageWithState();
                    this.clearManagedUrlParamsFromAddressBar();
                    return loadedFromUrl;
                }
                // URL has non-managed params only — fall through to localStorage
            }
            if (hasLocalStorageValue()) {
                const use = await useLocalStorage();
                if (!use)
                    return false;
                const loadedFromStorage = this.restoreFromLocalStorage(false);
                if (loadedFromStorage) {
                    this.syncLocalStorageWithState();
                    this.clearManagedUrlParamsFromAddressBar();
                }
                return loadedFromStorage;
            }
        }
        catch (err) {
            console.error("Failed to load state from URL or localStorage", err);
            return false;
        }
    }
    setQuantity(artCodeTSNL, quantity, emit = true, force = false) {
        const prev = this.getQuantity(artCodeTSNL);
        if (quantity < 1) {
            delete this.articles[artCodeTSNL];
            delete this.crosssellArticles[artCodeTSNL];
            if (emit)
                this.emit("quantity:change", { artCodeTSNL, quantity });
            return;
        }
        this.articles[artCodeTSNL] = { quantity };
        const variantProduct = Object.values(this.products.variants).find(v => v?.artCodeTSNL === artCodeTSNL);
        const product = variantProduct ?? find(artCodeTSNL, this.products.filtered)?.product;
        if (product) {
            const newOnderlegSet = "onderlegSet" in product && product.onderlegSet;
            const existingOnderlegSet = Object.keys(this.crosssellArticles[artCodeTSNL] ?? {}).find(code => onderlegSet.has(code));
            if (newOnderlegSet) {
                if (existingOnderlegSet && existingOnderlegSet !== newOnderlegSet) {
                    const existingQty = this.getCrosssellQuantity(artCodeTSNL, existingOnderlegSet);
                    this.setCrosssellQuantity(artCodeTSNL, existingOnderlegSet, 0, emit, true);
                    this.setCrosssellQuantity(artCodeTSNL, newOnderlegSet, existingQty || quantity || 1, emit);
                }
                else if (this.getCrosssellQuantity(artCodeTSNL, newOnderlegSet) < quantity || this.getCrosssellQuantity(artCodeTSNL, newOnderlegSet) === prev)
                    this.setCrosssellQuantity(artCodeTSNL, newOnderlegSet, quantity, emit);
            }
            else {
                if (existingOnderlegSet)
                    this.setCrosssellQuantity(artCodeTSNL, existingOnderlegSet, 0, emit, true);
            }
        }
        if (emit)
            this.emit("quantity:change", { artCodeTSNL, quantity });
    }
    getQuantity(artCodeTSNL) {
        const q = this.articles[artCodeTSNL]?.quantity ?? 0;
        if (isNaN(q))
            return 0;
        return q;
    }
    setCrosssellQuantity(parentCode, relatedCode, quantity, emit = true, force = false) {
        this.crosssellArticles[parentCode] ??= {};
        if (!force && onderlegSet.has(relatedCode)) {
            const q = this.getQuantity(parentCode);
            if (quantity < q) {
                this.crosssellArticles[parentCode][relatedCode] = { quantity: q };
                if (emit)
                    this.emit("quantity:change", { artCodeTSNL: relatedCode, parentCodeTSNL: parentCode, rejected: true, quantity: q });
                return;
            }
        }
        if (quantity < 1)
            delete this.crosssellArticles[parentCode][relatedCode];
        else
            this.crosssellArticles[parentCode][relatedCode] = { quantity };
        if (emit)
            this.emit("quantity:change", { artCodeTSNL: relatedCode, parentCodeTSNL: parentCode, quantity });
    }
    getCrosssellQuantity(parentCode, relatedCode) {
        const q = this.crosssellArticles[parentCode]?.[relatedCode]?.quantity ?? 0;
        return isNaN(q) ? 0 : q;
    }
    setWisselType(value, emit = true, force = false) {
        if (value == undefined)
            value = WisselType[0];
        if (!force && this.wissel.type.value === value)
            return;
        this.wissel.type.value = value;
        this.setHoekVerhouding(this.wissel.hoekVerhouding.value, emit, force);
        this.setBoogStraal(this.wissel.boogStraal.value, emit, force);
        this.setTabs();
        if (emit)
            this.emit("wissel:change", { value });
    }
    setWisselLigger(value, emit = true, force = false) {
        if (value == undefined)
            value = WisselLigger[0];
        if (!force && this.wissel.wisselLigger.value === value)
            return;
        this.wissel.wisselLigger.value = value;
        if (emit)
            this.emit("wisselLigger:change", { value });
    }
    setHoekVerhouding(value, emit = true, force = false) {
        if (this.wissel.type.value === "INTERSECTION_SWITCH")
            value = undefined;
        else if (value == undefined)
            value = HoekVerhouding[0];
        if (!force && this.wissel.hoekVerhouding.value === value)
            return;
        this.wissel.hoekVerhouding.value = value;
        if (emit)
            this.emit("hoekVerhouding:change", { value });
    }
    setSpoorStaafProfiel(value, emit = true, force = false) {
        if (value == undefined)
            value = SpoorStaafProfiel[0];
        if (!force && this.wissel.spoorStaafProfiel.value === value)
            return;
        this.wissel.spoorStaafProfiel.value = value;
        if (emit)
            this.emit("spoorStaafProfiel:change", { value });
    }
    setBoogStraal(value, emit = true, force = false) {
        if (this.wissel.type.value === "INTERSECTION_SWITCH")
            value = undefined;
        else if (value == undefined)
            value = BoogStraal[0];
        if (!force && this.wissel.boogStraal.value === value)
            return;
        this.wissel.boogStraal.value = value;
        if (emit)
            this.emit("boogStraal:change", { value });
    }
    setMobileTrackService(value) {
        this.mobileTrackService = value;
        this.emit("services:change", {});
    }
    setReturnOldProduct(value) {
        this.returnOldProduct = value;
        this.emit("services:change", {});
    }
    setProrailChoice(item) {
        if (!item) {
            delete this.prorailChoice;
            return;
        }
        this.prorailChoice = {
            key: item[IDX.key],
            equipmentId: item[IDX.equipmentId],
            hoekverhouding: item[IDX.hoekverhouding],
            profiel: item[IDX.profiel],
            boogstraal: item[IDX.boogstraal],
            smeerlozeConstructie: smMap[item[IDX.smeerlozeConstructie]],
            wisselligger: item[IDX.wisselligger],
            type: item[IDX.type],
            geocode: item[IDX.geocode],
            classificatie: item[IDX.classificatie],
            puntStukMateriaal: pmMap[item[IDX.afschrijvingsgroep]],
            plaatsingsdatum: item[IDX.plaatsingsdatum],
            tekeningnummer: item[IDX.tekeningnummer],
            afschrijvingsgroep: item[IDX.afschrijvingsgroep],
        };
    }
    getProrailChoice() {
        return this.prorailChoice;
    }
    setTabs() {
        this.tabs.length = 0;
        if (this.wissel.type.value === "INTERSECTION_SWITCH") {
            this.tabs.push(this.wissel.type, this.wissel.wisselLigger, this.wissel.spoorStaafProfiel);
        }
        else {
            this.tabs.push(this.wissel.type, this.wissel.wisselLigger, this.wissel.hoekVerhouding, this.wissel.spoorStaafProfiel, this.wissel.boogStraal);
        }
    }
    setAnswers(key, answers, emit = true) {
        this.answers[key] = answers;
        if (emit)
            this.emit("answers:change", { key, value: answers });
    }
    getAnswers(key) {
        return this.answers[key] ?? undefined;
    }
    async rebuild(emit = true) {
        const previousProducts = { ...this.products };
        this.products.filtered = filter(this.wissel.type.value, this.wissel.hoekVerhouding.value, this.wissel.wisselLigger.value, this.wissel.spoorStaafProfiel.value, this.wissel.boogStraal.value);
        this.products.base = getBaseProductList(this.wissel.type.value, this.wissel.hoekVerhouding.value, this.wissel.wisselLigger.value, this.wissel.spoorStaafProfiel.value, this.wissel.boogStraal.value).filter(Boolean);
        this.products.variants = {};
        this.setTabs();
        await this.rebuildVariants(previousProducts);
        if (emit)
            this.emit("rebuild", {});
    }
    async rebuildVariants(previousProducts) {
        const variants = previousProducts?.variants ?? {};
        this.products.variants = {};
        for (const k in this.answers) {
            const base = this.products.base.find(p => p.category === k);
            if (!base)
                continue;
            const subset = this.products.filtered[base.category];
            if (!subset)
                continue;
            const answers = this.answers[k];
            if (answers && Object.values(answers).every(v => v !== undefined)) {
                let result = _findByAnswers(subset, questions[base.category], answers)?.[0];
                result ??= _findClosest(subset, variants[k]);
                if (result)
                    this.products.variants[base.category] = result;
            }
        }
        for (const category in this.products.variants) {
            const variant = this.products.variants[category];
            if (!variant || !("onderlegSet" in variant) || !variant.onderlegSet)
                continue;
            const artCodeTSNL = variant.artCodeTSNL;
            if (!this.articles[artCodeTSNL])
                continue;
            const newOnderlegSet = variant.onderlegSet;
            const existingOnderlegSet = Object.keys(this.crosssellArticles[artCodeTSNL] ?? {}).find(code => onderlegSet.has(code));
            if (newOnderlegSet) {
                if (existingOnderlegSet && existingOnderlegSet !== newOnderlegSet) {
                    const existingQty = this.getCrosssellQuantity(artCodeTSNL, existingOnderlegSet);
                    this.setCrosssellQuantity(artCodeTSNL, existingOnderlegSet, 0, true, true);
                    this.setCrosssellQuantity(artCodeTSNL, newOnderlegSet, existingQty || this.getQuantity(artCodeTSNL) || 1, true);
                }
            }
            else if (existingOnderlegSet)
                this.setCrosssellQuantity(artCodeTSNL, existingOnderlegSet, 0, true, true);
        }
        // Variants are recalculated based on current answers and available products.
    }
    getVariant(category) {
        return this.products.variants[category];
    }
    rebuildAsync = debounce(100, async () => {
        await this.rebuild();
    });
    withSuppressedUrlSync(cb) {
        const prev = this.suppressUrlSync;
        this.suppressUrlSync = true;
        try {
            return cb();
        }
        finally {
            this.suppressUrlSync = prev;
        }
    }
    clearManagedUrlParams(params) {
        for (const key of State.URL_MANAGED_KEYS)
            params.delete(key);
    }
    clearManagedUrlParamsFromAddressBar() {
        const params = new URLSearchParams(window.location.search);
        this.clearManagedUrlParams(params);
        const search = params.toString();
        const url = `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`;
        const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        if (url !== current)
            window.history.replaceState({}, "", url);
    }
    parseRequiredOption(params, key, options) {
        const value = params.get(key);
        if (!value || !options.includes(value))
            throw new Error(`Invalid URL parameter ${key}`);
        return value;
    }
    parseOptionalOption(params, key, options) {
        const value = params.get(key);
        if (value == undefined || value === "")
            return undefined;
        if (!options.includes(value))
            throw new Error(`Invalid URL parameter ${key}`);
        return value;
    }
    encodeArticles() {
        return Object.entries(this.articles)
            .map(([code, { quantity }]) => [code, quantity])
            .filter(([, quantity]) => !!quantity && !isNaN(quantity))
            .sort(([left], [right]) => left.localeCompare(right))
            .map(([code, quantity]) => `${code}~${quantity}`)
            .join(",");
    }
    parseArticles(value) {
        const articles = {};
        for (const entry of (value ?? "").split(",").filter(Boolean)) {
            const [code, qty] = entry.split("~");
            const quantity = Number(qty);
            if (!code || !qty || !Number.isFinite(quantity) || quantity <= 0)
                continue;
            articles[code] = { quantity }; // todo use setQty fn?
        }
        return articles;
    }
    encodeRelatedQuantities() {
        return Object.entries(this.crosssellArticles)
            .sort(([a], [b]) => a.localeCompare(b))
            .flatMap(([parentCode, related]) => Object.entries(related)
            .filter(([, { quantity }]) => !!quantity && !isNaN(quantity))
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([relatedCode, { quantity }]) => `${parentCode}~${relatedCode}~${quantity}`))
            .join(",");
    }
    parseRelatedQuantities(value) {
        const result = {};
        for (const entry of (value ?? "").split(",").filter(Boolean)) {
            const [parentCode, relatedCode, qtyRaw] = entry.split("~");
            const qty = Number(qtyRaw);
            if (!parentCode || !relatedCode || !qtyRaw || !Number.isFinite(qty) || qty <= 0)
                continue;
            if (!result[parentCode])
                result[parentCode] = {};
            result[parentCode][relatedCode] = qty;
        }
        return result;
    }
    encodeAnswers() {
        return Object.entries(this.answers)
            .sort(([left], [right]) => left.localeCompare(right))
            .map(([category, answers]) => {
            const encoded = Object.entries((answers ?? {}))
                .map(([qi, ai]) => ai === undefined ? undefined : `${qi}-${ai}`)
                .filter(Boolean)
                .sort((left, right) => {
                const [leftQuestion = 0] = left.split("-").map(Number);
                const [rightQuestion = 0] = right.split("-").map(Number);
                return leftQuestion - rightQuestion;
            })
                .join(".");
            if (!encoded)
                return undefined;
            return `${category}~${encoded}`;
        })
            .filter(Boolean)
            .join(",");
    }
    parseAnswers(value) {
        const answers = {};
        for (const entry of (value ?? "").split(",").filter(Boolean)) {
            const [categoryRaw, rawPairs] = entry.split("~");
            if (!categoryRaw || !rawPairs || !(categoryRaw in questions))
                continue;
            const pairs = rawPairs
                .split(".")
                .map(pair => pair.split("-").map(Number))
                .filter(([qi, ai]) => Number.isInteger(qi) && Number.isInteger(ai) && qi >= 0 && ai >= 0)
                .sort(([leftQ], [rightQ]) => leftQ - rightQ);
            if (pairs.length)
                answers[categoryRaw] = Object.fromEntries(pairs.map(([qi, ai]) => [qi, ai]));
        }
        return answers;
    }
    toNormalizedUrlParams(base) {
        const params = base ? new URLSearchParams(base) : new URLSearchParams(window.location.search);
        this.clearManagedUrlParams(params);
        params.set(State.URL_KEYS.version, State.URL_VERSION);
        params.set(State.URL_KEYS.type, this.wissel.type.value);
        params.set(State.URL_KEYS.wisselLigger, this.wissel.wisselLigger.value);
        if (this.wissel.hoekVerhouding.value != undefined)
            params.set(State.URL_KEYS.hoekVerhouding, this.wissel.hoekVerhouding.value);
        if (this.wissel.spoorStaafProfiel.value != undefined)
            params.set(State.URL_KEYS.spoorStaafProfiel, this.wissel.spoorStaafProfiel.value);
        if (this.wissel.boogStraal.value != undefined)
            params.set(State.URL_KEYS.boogStraal, this.wissel.boogStraal.value);
        if (this.prorailChoice?.key)
            params.set(State.URL_KEYS.prorailKey, this.prorailChoice.key);
        if (this.prorailChoice?.equipmentId)
            params.set(State.URL_KEYS.prorailEquipmentId, this.prorailChoice.equipmentId);
        if (this.prorailChoice?.smeerlozeConstructie)
            params.set(State.URL_KEYS.smeerlozeConstructie, this.prorailChoice.smeerlozeConstructie);
        if (this.prorailChoice?.puntStukMateriaal)
            params.set(State.URL_KEYS.puntStukMateriaal, this.prorailChoice.puntStukMateriaal);
        const encodedArticles = this.encodeArticles();
        if (encodedArticles)
            params.set(State.URL_KEYS.article, encodedArticles);
        const encodedRelated = this.encodeRelatedQuantities();
        if (encodedRelated)
            params.set(State.URL_KEYS.crosssellArticle, encodedRelated);
        const encodedAnswers = this.encodeAnswers();
        if (encodedAnswers)
            params.set(State.URL_KEYS.answer, encodedAnswers);
        if (!this.mobileTrackService)
            params.set(State.URL_KEYS.mobileTrackService, "0");
        if (this.returnOldProduct)
            params.set(State.URL_KEYS.returnOldProduct, "1");
        return params;
    }
    parseNormalizedUrlParams(params) {
        const hasManagedParams = State.URL_MANAGED_KEYS.some(key => params.has(key));
        if (!hasManagedParams)
            return undefined;
        const version = params.get(State.URL_KEYS.version);
        if (version !== State.URL_VERSION)
            throw new Error(`Invalid URL parameter ${State.URL_KEYS.version}`);
        const type = this.parseRequiredOption(params, State.URL_KEYS.type, WisselType);
        const wisselLigger = this.parseRequiredOption(params, State.URL_KEYS.wisselLigger, WisselLigger);
        const hoekVerhouding = this.parseOptionalOption(params, State.URL_KEYS.hoekVerhouding, HoekVerhouding);
        const spoorStaafProfiel = this.parseOptionalOption(params, State.URL_KEYS.spoorStaafProfiel, SpoorStaafProfiel);
        const boogStraal = this.parseOptionalOption(params, State.URL_KEYS.boogStraal, BoogStraal);
        const smeerlozeConstructieValue = this.parseOptionalOption(params, State.URL_KEYS.smeerlozeConstructie, smeerlozeConstructie);
        const puntStukMateriaalValue = this.parseOptionalOption(params, State.URL_KEYS.puntStukMateriaal, ["MN", "HT"]);
        const articles = this.parseArticles(params.get(State.URL_KEYS.article));
        const relatedArticle = this.parseRelatedQuantities(params.get(State.URL_KEYS.crosssellArticle));
        const answers = this.parseAnswers(params.get(State.URL_KEYS.answer));
        const prorailKey = params.get(State.URL_KEYS.prorailKey) ?? undefined;
        const prorailEquipmentId = params.get(State.URL_KEYS.prorailEquipmentId) ?? undefined;
        const mobileTrackServiceRaw = params.get(State.URL_KEYS.mobileTrackService);
        const returnOldProductRaw = params.get(State.URL_KEYS.returnOldProduct);
        return {
            wissel: {
                wisselType: type,
                wisselLigger,
                hoekVerhouding: hoekVerhouding ?? undefined,
                spoorStaafProfiel: spoorStaafProfiel ?? SpoorStaafProfiel[0],
                boogStraal: boogStraal ?? undefined,
            },
            article: articles,
            relatedArticle,
            answer: answers,
            smeerlozeConstructie: smeerlozeConstructieValue ?? undefined,
            puntStukMateriaal: puntStukMateriaalValue ?? undefined,
            prorailKey,
            prorailEquipmentId,
            mobileTrackService: mobileTrackServiceRaw === null ? true : mobileTrackServiceRaw !== "0",
            returnOldProduct: returnOldProductRaw === "1",
        };
    }
    readUrlState() {
        const params = new URLSearchParams(window.location.search);
        return this.parseNormalizedUrlParams(params);
    }
    readLocalStorageState() {
        const raw = getLocalStorageValue();
        if (!raw)
            return undefined;
        const params = new URLSearchParams(raw);
        return this.parseNormalizedUrlParams(params);
    }
    getStateSignature() {
        return this.toNormalizedUrlParams().toString();
    }
    applyArticles(article, emit) {
        for (const key in article)
            this.setQuantity(key, article[key].quantity, emit, true);
    }
    applyRelatedArticles(relatedArticle, emit) {
        for (const parentCode in relatedArticle)
            for (const relatedCode in relatedArticle[parentCode])
                this.setCrosssellQuantity(parentCode, relatedCode, relatedArticle[parentCode][relatedCode], emit);
    }
    applyAnswers(answer, emit) {
        for (const key in answer)
            this.setAnswers(key, answer[key], emit);
    }
    applyUrlState(parsed, emit = true) {
        this.withSuppressedUrlSync(() => {
            const { wissel, article, relatedArticle, answer, prorailKey, prorailEquipmentId } = parsed;
            this.resetState(emit, true);
            this.setWisselType(wissel.wisselType, emit, true);
            this.setWisselLigger(wissel.wisselLigger, emit, true);
            this.setHoekVerhouding(wissel.hoekVerhouding, emit, true);
            this.setSpoorStaafProfiel(wissel.spoorStaafProfiel, emit, true);
            this.setBoogStraal(wissel.boogStraal, emit, true);
            this.applyArticles(article, emit);
            if (relatedArticle)
                this.applyRelatedArticles(relatedArticle, emit);
            this.applyAnswers(answer, emit);
            if (parsed.mobileTrackService !== undefined)
                this.setMobileTrackService(parsed.mobileTrackService);
            if (parsed.returnOldProduct !== undefined)
                this.setReturnOldProduct(parsed.returnOldProduct);
            if (prorailKey && prorailEquipmentId) {
                this.rebuild(emit);
                import("./data/prorail")
                    .then(x => x.loadItems())
                    .then(items => {
                    const found = items.find(i => i[IDX.key] === prorailKey && i[IDX.equipmentId] === prorailEquipmentId);
                    this.setProrailChoice(found);
                })
                    .catch(err => {
                    console.error("Failed to load prorail data:", err);
                })
                    .finally(() => {
                    this.prevUrlSignature = this.getStateSignature();
                    this.syncUrlWithState("replace");
                    this.syncLocalStorageWithState();
                });
            }
            else {
                this.rebuild(emit);
                this.prevUrlSignature = this.getStateSignature();
                this.syncUrlWithState("replace");
                this.syncLocalStorageWithState();
            }
        });
    }
    restoreFromCurrentUrl(emit = true) {
        const parsed = this.readUrlState();
        if (!parsed)
            return false;
        this.applyUrlState(parsed, emit);
        return true;
    }
    restoreFromLocalStorage(emit = true) {
        const parsed = this.readLocalStorageState();
        if (!parsed)
            return false;
        this.applyUrlState(parsed, emit);
        return true;
    }
    applyNormalizedUrlState(parsed, emit = true) {
        this.applyUrlState(parsed, emit);
    }
    getURL() {
        // build fresh, ignore any stale query the address bar carries since syncUrlWithState is disabled
        const lang = new URLSearchParams(window.location.search).get("lang");
        const base = new URLSearchParams();
        if (lang)
            base.set("lang", lang);
        const params = this.toNormalizedUrlParams(base);
        const search = params.toString();
        return `${window.location.origin}${window.location.pathname}${search ? `?${search}` : ""}`;
    }
    syncUrlWithState(mode = "replace") {
        return; // disabled for now, conflicts with localstorage loading
        // const params = this.toNormalizedUrlParams()
        // const search = params.toString()
        // const url = `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`
        // const current = `${window.location.pathname}${window.location.search}${window.location.hash}`
        // if (url === current)
        //   return
        // if (mode === "push")
        //   window.history.pushState({}, "", url)
        // else
        //   window.history.replaceState({}, "", url)
    }
    syncLocalStorageWithState() {
        try {
            const serializedState = this.toNormalizedUrlParams().toString();
            setLocalStorageValue(serializedState);
        }
        catch (err) {
            console.error("Failed to sync state to localStorage", err);
        }
    }
    resetState(emit, force) {
        this.prorailChoice = undefined;
        this.mobileTrackService = true;
        this.returnOldProduct = false;
        this.setWisselType(WisselType[0], emit, force);
        this.setWisselLigger(WisselLigger[0], emit, force);
        this.setHoekVerhouding(HoekVerhouding[0], emit, force);
        this.setSpoorStaafProfiel(SpoorStaafProfiel[0], emit, force);
        this.setBoogStraal(BoogStraal[0], emit, force);
        for (const key in this.answers)
            delete this.answers[key];
        for (const key in this.articles)
            this.setQuantity(key, 0, emit, force);
        for (const key in this.crosssellArticles)
            delete this.crosssellArticles[key];
    }
    async reset(emit = true, force = false) {
        this.withSuppressedUrlSync(() => this.resetState(emit, force));
        await this.rebuild(emit);
        this.prevUrlSignature = this.getStateSignature();
        this.syncUrlWithState("replace");
        this.syncLocalStorageWithState();
    }
    getSummaryList() {
        const type = parseOption(this.wissel.type.value);
        const dwarsligger = parseOption(this.wissel.wisselLigger.value);
        const hoek = parseOption(this.wissel.hoekVerhouding.value);
        const profiel = parseOption(this.wissel.spoorStaafProfiel.value);
        const straal = parseOption(this.wissel.boogStraal.value);
        return [
            [type.option, ...Object.values(type.properties).map(p => p.value)],
            [dwarsligger.option],
            [hoek.option],
            [profiel.option],
            [straal.option],
        ]
            .filter(x => x[0])
            .map(x => x.reduce((s, x, i, a) => s
            + (i === 1 ? " (" : "")
            + (typeof x === "string" ? getText(x) : x)
            + (a.length > 1 && i === a.length - 1 ? ")" : ""), ""));
    }
    getSummary() {
        return this.getSummaryList().join(" | ");
    }
    getServicesArticles() {
        const result = {};
        if (this.mobileTrackService)
            result["INZET MBS"] = 1;
        if (this.returnOldProduct)
            result["OUD PRODUCT RETOUR"] = 1;
        return result;
    }
}
const state = new State();
export default state;
window["state"] = state;
